import type { UseQueryResult } from "@tanstack/react-query";
import { useId } from "react";
import type { JSX } from "react";
import { Link } from "react-router";
import { routePaths } from "../../app/navigation";
import { formatHoursMinutes } from "../../domain/duration";
import type { LocalDate } from "../../domain/time";
import { useI18n } from "../../shared/i18n/I18nContext";
import { useMe } from "../../shared/session/useMe";
import { cx } from "../../shared/ui/classNames";
import { Skeleton } from "../../shared/ui/Skeleton";
import styles from "../../shared/ui/ui.module.css";
import { useAbsences } from "../absences/useAbsences";
import { useBalances } from "../balances/useBalances";
import { useCorrections } from "../corrections/useCorrections";
import { useAttributionRequests, useProjectDays } from "../projects/useAttributionRequests";
import { periodRange } from "../times/model/period";
import { useWorkDays } from "../times/useWorkDays";
import {
  absenceRequestRange,
  balanceFigures,
  openItems,
  openProjectDayRange,
} from "./model/todayOverview";
import todayStyles from "./TodayOverview.module.css";

/** How a query of the overview stands. */
type QueryAvailability = "loading" | "offline" | "failed" | "ready";

/**
 * Returns how a query stands: it waits for a connection while it is paused without data.
 *
 * @param query - The query.
 * @returns The availability.
 */
function availabilityOf(query: UseQueryResult): QueryAvailability {
  let availability: QueryAvailability = "ready";

  if (query.data === undefined) {
    if (query.isError) {
      availability = "failed";
    } else if (query.fetchStatus === "paused") {
      availability = "offline";
    } else {
      availability = "loading";
    }
  }

  return availability;
}

/** Properties of {@link TodayOverview}. */
export interface TodayOverviewProps {
  /** Today in the time zone of the employment. */
  readonly today: LocalDate;
  /** Time zone of the employment. */
  readonly timeZone: string;
}

/**
 * The balance and what is open on the start page „Heute“ (F-75): the flexitime and overtime
 * accounts (or the month's actual and target time without a time account) as of today, and own
 * requests waiting for a decision (corrections, absences also for past days, project attributions in
 * locked periods, F-78), findings of this week and days still lacking a project attribution, each
 * with a link to its page. Everything comes from the queries of those pages, so
 * they share the answers; without a connection the overview says so, the clock above keeps working.
 *
 * @param props - Component properties.
 * @returns The two sections.
 */
export function TodayOverview(props: TodayOverviewProps): JSX.Element {
  const { t } = useI18n();
  const balanceId = useId();
  const openId = useId();
  const me = useMe();
  const balances = useBalances(props.today);
  const corrections = useCorrections();
  const absences = useAbsences(absenceRequestRange(props.today));
  const workDays = useWorkDays(periodRange("week", props.today));
  const openRange = openProjectDayRange(props.today);
  const projectDays = useProjectDays(openRange.from, openRange.to);
  // Requests for attributions in locked periods exist only with project time tracking (F-78).
  const projectsAvailable = projectDays.data?.isAvailable === true;
  const attributionRequests = useAttributionRequests("pending", projectsAvailable);
  const items = openItems({
    employeeId: me.data?.employeeId ?? null,
    corrections: corrections.data,
    absences: absences.data,
    attributionRequests: projectsAvailable ? attributionRequests.data : undefined,
    workDays: workDays.data,
    projectDays: projectDays.data,
  });
  const openQueries: UseQueryResult[] = [me, corrections, absences, workDays, projectDays];

  if (projectsAvailable) {
    openQueries.push(attributionRequests);
  }

  const openSources = openQueries.map(availabilityOf);
  const openOffline = openSources.includes("offline");
  const openFailed = openSources.includes("failed");
  const openLoading = openSources.includes("loading");
  const balanceState = availabilityOf(balances);
  const hours = (seconds: number): string => `${formatHoursMinutes(seconds)}\u00a0h`;

  const unavailable = (state: QueryAvailability): JSX.Element | null =>
    state === "offline" ? (
      <p className={styles["muted"]}>{t("today.offline")}</p>
    ) : state === "failed" ? (
      <p className={styles["muted"]}>{t("today.failed")}</p>
    ) : null;

  const overview = (
    <>
      <section aria-labelledby={balanceId} className={cx(styles["card"], styles["stack"])}>
        <h2 id={balanceId}>{t("today.balance.title")}</h2>
        {balanceState === "loading" ? <Skeleton shape="lines" /> : unavailable(balanceState)}
        {balances.data === undefined ? null : (
          <dl className={todayStyles["figures"]}>
            {balanceFigures(balances.data).map((figure) => (
              <div key={figure.kind}>
                <dt>{t(`today.balance.${figure.kind}`)}</dt>
                <dd>{hours(figure.seconds)}</dd>
              </div>
            ))}
          </dl>
        )}
        <Link to={routePaths.balances}>{t("today.balance.link")}</Link>
      </section>

      <section aria-labelledby={openId} className={cx(styles["card"], styles["stack"])}>
        <h2 id={openId}>{t("today.open.title")}</h2>
        {items.length === 0 ? null : (
          <ul className={styles["stack"]}>
            {items.map((item) => (
              <li key={item.kind}>
                <Link to={item.path}>{t(`today.open.${item.kind}`, { count: item.count })}</Link>
              </li>
            ))}
          </ul>
        )}
        {items.length === 0 && openLoading && !openOffline && !openFailed ? (
          <Skeleton shape="lines" />
        ) : null}
        {items.length === 0 && !openLoading && !openOffline && !openFailed ? (
          <p className={styles["muted"]}>{t("today.open.none")}</p>
        ) : null}
        {openOffline ? unavailable("offline") : openFailed ? unavailable("failed") : null}
      </section>
    </>
  );

  return overview;
}
